import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";

export interface TeamMember {
  id: number;
  public_name: string;
}

export interface Team {
  id: number;
  name: string;
  members: TeamMember[];
  is_member: boolean;
  is_captain: boolean;
  created_at: string;
}

export const teamsKey = ["teams"] as const;

export function useTeams() {
  return useQuery({
    queryKey: teamsKey,
    queryFn: () => api<{ count: number; results: Team[] }>("/api/teams/"),
  });
}

function useTeamsInvalidation() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: teamsKey });
}

export function useCreateTeam() {
  const invalidate = useTeamsInvalidation();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      api<Team>("/api/teams/", { method: "POST", json: body }),
    onSuccess: invalidate,
  });
}

export function useRenameTeam() {
  const invalidate = useTeamsInvalidation();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api<Team>(`/api/teams/${id}/`, { method: "PATCH", json: { name } }),
    onSuccess: invalidate,
  });
}

export function useAddMember() {
  const invalidate = useTeamsInvalidation();
  return useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) =>
      api<TeamMember>(`/api/teams/${id}/members/`, { method: "POST", json: { email } }),
    onSuccess: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useTeamsInvalidation();
  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) =>
      api<void>(`/api/teams/${id}/members/${userId}/`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}
